import { Injectable, inject } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { Catalog, CatalogExtraImageWithTag } from '@cureskin/api-client/src/server-api/api';

/** Tag used on Catalog.extraImagesWithTags for the artwork of the AOV free product. */
const AOV_PRODUCT_IMAGE_TAG = 'AOV';

/** Variant shape of the `aov_gamification` experiment. */
export interface AovGamificationVariant {
  productID?: string;
  productImage?: string;
  totalValue?: number;
  bannerImagesMultiple?: Record<string, string>;
}

export interface AovFreeProduct {
  /** Catalog entry of the free product; undefined when it could not be fetched. */
  catalog?: Catalog;
  /** The single image every AOV surface renders for the free product. */
  image?: string;
}

/**
 * Owns everything about the AOV (free travel-pouch) promotion product. Every AOV
 * surface — shop, reorder, cart, recently-viewed — resolves the free product through
 * this service instead of re-deriving the catalog fetch and image fallback chain.
 */
@Injectable({ providedIn: 'root' })
export class AovService {
  private readonly conn = inject(ConnectionService);
  private readonly experimentService = inject(ExperimentService);

  /** Fetches the AOV free product and resolves the image to render for it. */
  async getFreeProduct(variant: AovGamificationVariant): Promise<AovFreeProduct> {
    const catalog = await this.fetchCatalog(variant?.productID);
    return { catalog, image: await this.resolveImage(catalog, variant) };
  }

  /** The AOV-tagged artwork ships with the order_flow_header redesign only. */
  private async isTaggedImageEnabled(): Promise<boolean> {
    await this.experimentService.experimentsLoaded;
    return this.experimentService.isOrderFlowHeaderEnabled();
  }

  private async fetchCatalog(productId?: string): Promise<Catalog | undefined> {
    if (!productId) {
      return undefined;
    }
    const products = await this.conn.findCatalogWithKey([productId]);
    const [product] = JSON.parse(JSON.stringify(products || []));
    return product;
  }

  /** Under order_flow_header the AOV-tagged image wins; everywhere else the legacy chain is the only source. */
  private async resolveImage(catalog: Catalog | undefined, variant: AovGamificationVariant): Promise<string | undefined> {
    const taggedImage = await this.isTaggedImageEnabled() ? this.pickTaggedImage(catalog) : undefined;
    return taggedImage
      || catalog?.rebrandedImageWithoutBackground?.[0]
      || variant?.productImage
      || catalog?.rebrandedImageWithBackground?.[0]
      || catalog?.productUnboxedImage?.[0];
  }

  /** Highest-priority `AOV`-tagged entry of Catalog.extraImagesWithTags. */
  private pickTaggedImage(catalog: Catalog | undefined): string | undefined {
    const taggedImage = catalog?.extraImagesWithTags
      ?.filter((image: CatalogExtraImageWithTag): boolean => image?.tag === AOV_PRODUCT_IMAGE_TAG && !!image?.imageLink)
      .reduce<CatalogExtraImageWithTag | undefined>(
      (highest: CatalogExtraImageWithTag | undefined, image: CatalogExtraImageWithTag): CatalogExtraImageWithTag => (
        !highest || (image.priority || 0) > (highest.priority || 0) ? image : highest),
      undefined,
    );
    return taggedImage?.imageLink;
  }
}
